import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const YAMLParserConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['operation', 'input']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const operations = [
    { value: 'parse', label: 'YAML → JSON', icon: '📋', desc: 'YAML szöveg JSON-né alakítása' },
    { value: 'stringify', label: 'JSON → YAML', icon: '📄', desc: 'JSON adatok YAML-lé alakítása' },
  ];

  return (
    <div className="space-y-3">
      {/* Művelet típus */}
      <CollapsibleSection
        id="operation"
        title="Művelet Típusa"
        icon="🔄"
        color="cyan"
        isExpanded={expandedSections.has('operation')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            {operations.map((op) => (
              <button
                key={op.value}
                onClick={() => updateConfig('operation', op.value)}
                className={`flex items-center p-3 rounded-md border text-sm transition-all ${
                  (node.data.config.operation || 'parse') === op.value
                    ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <span className="text-xl mr-3">{op.icon}</span>
                <div className="flex-1 text-left">
                  <div className="font-medium">{op.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{op.desc}</div>
                </div>
                {(node.data.config.operation || 'parse') === op.value && (
                  <span className="text-cyan-500">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Input */}
      <CollapsibleSection
        id="input"
        title="Tartalom"
        icon="📄"
        color="blue"
        isExpanded={expandedSections.has('input')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              {node.data.config.operation === 'parse' ? 'YAML szöveg' : 'JSON adat'}
            </label>
            <textarea
              value={node.data.config.input || ''}
              onChange={(e) => updateConfig('input', e.target.value)}
              className={`${commonStyles.textarea} font-mono text-xs`}
              rows={8}
              placeholder={node.data.config.operation === 'parse' 
                ? `name: John Doe
age: 30
address:
  city: New York
  zip: 10001
skills:
  - JavaScript
  - React

vagy {{yaml_data}}`
                : '{{data}} vagy JSON objektum'
              }
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {node.data.config.operation === 'parse' ? 'YAML szöveg vagy változó' : 'JSON adat vagy változó'}
            </p>
          </div>

          {node.data.config.operation === 'stringify' && (
            <div>
              <label className={commonStyles.label}>
                Behúzás mérete
              </label>
              <input
                type="number"
                value={node.data.config.indent || 2}
                onChange={(e) => updateConfig('indent', parseInt(e.target.value))}
                className={commonStyles.input}
                min="2"
                max="8"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Szóközök száma a behúzáshoz (2-8)
              </p>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A feldolgozás után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{json}}', desc: node.data.config.operation === 'parse' ? 'JSON objektum' : 'YAML szöveg', example: node.data.config.operation === 'parse' ? '{...}' : 'name: ...' },
              { var: '{{parsed}}', desc: 'Feldolgozva', example: 'true' },
              { var: '{{size}}', desc: 'Méret (byte)', example: '1024' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📋 Config fájl:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Fájl olvasás (YAML) → YAML Parser → JSON Processor
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🔄 Formátum konverzió:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Database Query → YAML Parser (stringify) → Fájl írás
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>⚙️ Kubernetes config:</strong>
            <div className="mt-1 font-mono text-[10px]">
              YAML Parser → Data Transform → HTTP Request
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default YAMLParserConfig;
